import { useEffect, useMemo, useState } from "react";
import React from "react";
import { useTheme } from "next-themes";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { type Container, type ISourceOptions } from "@tsparticles/engine";
import { loadStarsPreset } from "@tsparticles/preset-stars";
import { loadStarShape } from "@tsparticles/shape-star";
import { loadExternalAttractInteraction } from "@tsparticles/interaction-external-attract";

const ParticleBackground = () => {
  const { resolvedTheme, theme } = useTheme();
  const [init, setInit] = useState(false);

  // 在控制台输出当前主题信息以便调试
  useEffect(() => {
    console.log("当前主题信息:", { resolvedTheme, theme });
  }, [resolvedTheme, theme]);

  // 只在应用生命周期内初始化一次
  useEffect(() => {
    initParticlesEngine(async (engine) => {
      // 加载星星预设
      await loadStarsPreset(engine);
      // 加载星星形状
      await loadStarShape(engine);
      // 加载鼠标吸引交互
      await loadExternalAttractInteraction(engine);
    }).then(() => {
      setInit(true);
    });
  }, []);

  const particlesLoaded = async (container?: Container): Promise<void> => {
    console.log("粒子加载完成", container);
  };

  const options: ISourceOptions = useMemo(
    () => ({
      // 如果您想完全自定义形状，可以移除preset设置
      // preset: "stars",
      fullScreen: {
        enable: true,
        zIndex: -1
      },
      background: {
        color: "transparent",
      },
      interactivity: {
        detectsOn: "window",
        events: {
          onHover: {
            enable: true,
            mode: "attract"
          },
          resize: { enable: true }
        },
        modes: {
          attract: {
            distance: 75,
            duration: 0.3,
            easing: "ease-out-cubic",
            factor: 0.3,
            maxSpeed: 20,
            speed: 0.5
          }
        }
      },
      particles: {
        shape: {
          type: "star", // 设置形状为星形
          options: {
            star: {
              sides: 4, // 星星的边数
              inset: 3  // 星星的内凹程度
            }
          }
        },
        color: {
          value: resolvedTheme === "dark" || theme === "dark"
            ? ["#ffffff", "#f8f7ff", "#e4e1ff"] // 暗色主题：白色到浅紫色
            : ["#ffffff", "#f8f7ff", "#e4e1ff"],
            // : ["#000080", "#0000cd", "#4169e1"], // 亮色主题：蓝色系
        },
        number: {
          value: 80,
          density: {
            enable: true,
            area: 500
          }
        },
        opacity: {
          value: { min: 0.1, max: 0.8 },
          animation: {
            enable: true,
            speed: 0.5,
            minimumValue: 0.1
          }
        },
        size: {
          value: { min: 1, max: 3 }
        },
        move: {
          enable: true,
          speed: 0.4,
          direction: "none",  // 不指定方向
          random: true,       // 启用随机移动
          straight: false,    // 禁用直线移动
          // outModes: {
          //   default: "bounce" // 碰到边界时反弹
          // },
          // attract: {
          //   enable: false     // 禁用粒子之间的相互吸引
          // },
          warp: true,         // 启用扭曲效果
          trail: {
            enable: false
          },
          // gravity: {
          //   enable: true,
          //   acceleration: 0.05,
          //   inverse: false
          // },
          bounce: {
            enable: true,
            factor: 1.2
          }
        }
      }
    }),
    [resolvedTheme, theme]
  );

  if (!init) return null;

  return (
    <Particles
      id="tsparticles"
      particlesLoaded={particlesLoaded}
      options={options}
    />
  );
};

export default ParticleBackground;